import {Cache} from "aws-amplify"
import {queryClient} from "libs/query"
import liff from "@line/liff"
import {navigate} from "gatsby"
import * as jwt from "jose"

import {QUERY_KEY_GIANT_USER} from "../appCenter/hooks"
import {awsConfig} from "../../libs/aws/config"
import {changeLineMenuRequest} from "./requests"
import {AvailableLoginWay, CognitoUserIdentities, FederatedSignInCustomState} from "./schemas"
import {IdentityLoginWayMap} from "services/constants"

export const KEY_PREFIX = "-user-pool-web-client-id"
const KEY_CALLBACK_CLIENT = "-giant-id-callbackClient-id"
const KEY_CALLBACK_CLIENT_URI = "-giant-id-callbackClient-uri"
const KEY_CALLBACK_CLIENT_STATE = "-giant-id-callbackClient-state"
const KEY_LOGIN_REDIRECT = "-login-redirect"
const KEY_LINE_BINDING = "-line-account-bind"
const KEY_LIFF_INIT = "-liff-init"
const KEY_FEDERATED_SIGN_IN_STATE = "-federated-sign-in-state"

export function getWebClient() {
  return awsConfig.Auth.userPoolWebClientId
}

/**
 * @param {string} key
 * @param {any} value
 */
export function setItem(key = "", value) {
  return Cache.setItem(`-${getWebClient()}-${key}`, value)
}

/**
 * @param {string} key
 */
export function getItem(key = "") {
  return Cache.getItem(`-${getWebClient()}-${key}`)
}

/**
 * @param {{
 *   way: import('services/auth/types').TAvailableLoginWay,
 *   sub: string,
 *   username: string,
 * }} param
 */
export function setLoginInfo(param) {
  setItem(`login-way`, param.way)
  setItem(`sub-${param.way}`, param.sub)
  setItem(`username-${param.way}`, param.username)
}

export async function resetCache() {
  let result = Cache.getAllKeys()
  /** @type {string[]} */
  let keys = Array(0)

  if (Array.isArray(result)) {
    keys = result
  } else {
    keys = await result
  }

  if (Array.isArray(keys)) {
    keys
      .filter(key => key.startsWith(`-${getWebClient()}`))
      .forEach(Cache.removeItem)
  }
}

/**
 * @param {CognitoUser} user
 */
export function setLoginInfoIfNotExisted(user) {
  const hasLoginWay = !!getLoginWay()

  if (!hasLoginWay) {
    const identities = CognitoUserIdentities.parse(
      JSON.parse(user.attributes.identities ?? "[]"),
    )

    const loginWay =
      IdentityLoginWayMap[identities[0]?.providerName] ??
      AvailableLoginWay.enum.General

    if (user.attributes.sub && user.getUsername()) {
      setLoginInfo({
        way: loginWay,
        sub: user.attributes.sub,
        username: user.getUsername(),
      })
    }
  }
}

/**
 * @returns {import('services/auth/types').TAvailableLoginWay | null}
 */
export function getLoginWay() {
  return getItem(`login-way`)
}

/**
 * @param {import('services/auth/types').TAvailableLoginWay} way
 */
export function getLoginUsernameByWay(way) {
  return getItem(`username-${way}`)
}

/**
 * @param {import('services/auth/types').TAvailableSocialProvider} providerName the provider name to remove
 */
export function restoreUser(providerName) {
  const loginWay = getLoginWay()

  // remove the provider name from local storage
  if (loginWay && providerName) {
    const thirdPartyCognitoKeys = Object.keys(window?.localStorage ?? {})
      .filter(
        key =>
          key.indexOf(`CognitoIdentityServiceProvider.${getWebClient()}`) !==
          -1,
      )
      .filter(key => key.indexOf(providerName.toLowerCase()) !== -1)

    if (thirdPartyCognitoKeys.length) {
      thirdPartyCognitoKeys.forEach(thirdPartyCognitoKey => {
        window?.localStorage?.removeItem(thirdPartyCognitoKey)
      })
    }
  }

  if (loginWay) {
    const username = getLoginUsernameByWay(loginWay)
    window?.localStorage?.setItem(
      `CognitoIdentityServiceProvider.${getWebClient()}.LastAuthUser`,
      username,
    )
    queryClient.invalidateQueries(QUERY_KEY_GIANT_USER)
  }
}

/**
 *
 * @param {string} clientId
 */
export async function clearLoginInfoByClientId(clientId) {
  const keys = Object.keys(window?.localStorage ?? {}).filter(
    key => key.indexOf(`CognitoIdentityServiceProvider.${clientId}`) !== -1,
  )
  if (keys.length) {
    keys.forEach(key => {
      window?.localStorage?.removeItem(key)
    })
  }
}

/**
 * @typedef {{
 *   attributes: {sub: string, identities?: string }
 * } & import('amazon-cognito-identity-js').CognitoUser} CognitoUser
 */

/**
 * @param {string} url
 */
export function registerLoginRedirect(url) {
  Cache.setItem(KEY_LOGIN_REDIRECT, url, {
    // expires in 20 minutes
    expires: Date.now() + 20 * 60 * 1000,
  })
}

export function unregisterLoginRedirect() {
  Cache.removeItem(KEY_LOGIN_REDIRECT)
}

/**
 * @returns {string}
 */
export const getLoginRedirectUrl = () => Cache.getItem(KEY_LOGIN_REDIRECT)

export const hasLoginRedirect = () => !!Cache.getItem(KEY_LOGIN_REDIRECT)

export const handleLoginRedirect = () => {
  if (hasLoginRedirect()) {
    const redirectTo = getLoginRedirectUrl()
    unregisterLoginRedirect()
    navigate(redirectTo)
    return true
  }
  return false
}

export function registerLineBinding() {
  Cache.setItem(KEY_LINE_BINDING, true, {
    // expires in 20 minutes
    expires: Date.now() + 20 * 60 * 1000,
  })
}

export function unregisterLineBinding() {
  Cache.removeItem(KEY_LINE_BINDING)
}
export const hasLineBinding = () => !!Cache.getItem(KEY_LINE_BINDING)

export const handleLineBinding = async () => {
  if (hasLineBinding()) {
    unregisterLineBinding()
    await liff.ready
    if (liff.isInClient() && liff.isLoggedIn()) {
      await changeLineMenuRequest("authed")
        .then(() => {})
        .catch(err => {
          console.error("Change Line Menu Failed", err)
        })
      // always close the LIFF app
      liff.closeWindow()
      return true
    }
  }
  return false
}

export function registerLiffInit() {
  Cache.setItem(KEY_LIFF_INIT, true, {
    // expires in 20 minutes
    expires: Date.now() + 20 * 60 * 1000,
  })
}

export function unregisterLiffInit() {
  Cache.removeItem(KEY_LIFF_INIT)
}
export const shouldLiffInit = () => !!Cache.getItem(KEY_LIFF_INIT)

/**
 *
 * @param {string} idToken The ID token from cognito
 * @param {string} accessToken The access token from cognito
 * @param {string} refreshToken The refresh token from cognito
 * @param {string} [clientId] The Cognito client that issued the tokens
 */
export const injectCognitoToken = (
  idToken,
  accessToken,
  refreshToken,
  clientId = getWebClient(),
) => {
  const cognitoStorageKey = `CognitoIdentityServiceProvider.${clientId}`

  const idTokenJwt = jwt.decodeJwt(idToken)
  const accessTokenJwt = jwt.decodeJwt(accessToken)

  /**
   *
   * @param {number} iatAccessToken
   * @param {number} iatIdToken
   * @returns
   */
  const calculateClockDrift = (iatAccessToken, iatIdToken) => {
    const now = Math.floor(new Date().getTime() / 1000)
    const iat = Math.min(iatAccessToken, iatIdToken)
    return now - iat
  }

  window.localStorage.setItem(
    `${cognitoStorageKey}.LastAuthUser`,
    `${idTokenJwt["cognito:username"]}`,
  )
  window.localStorage.setItem(
    `${cognitoStorageKey}.${idTokenJwt["cognito:username"]}.idToken`,
    idToken,
  )
  window.localStorage.setItem(
    `${cognitoStorageKey}.${idTokenJwt["cognito:username"]}.accessToken`,
    accessToken,
  )
  window.localStorage.setItem(
    `${cognitoStorageKey}.${idTokenJwt["cognito:username"]}.refreshToken`,
    refreshToken,
  )
  window.localStorage.setItem(
    `${cognitoStorageKey}.${idTokenJwt["cognito:username"]}.clockDrift`,
    // @ts-ignore
    `${calculateClockDrift(accessTokenJwt["iat"], idTokenJwt["iat"])}`,
  )
}

export const shouldCloseWindow = () => {
  return !!Cache.getItem("closeWindow")
}

export const registerCloseWindow = () => {
  Cache.setItem("closeWindow", true, {
    // expires in 30 seconds
    expires: Date.now() + 30 * 1000,
  })
}

export const unregisterCloseWindow = () => {
  Cache.removeItem("closeWindow")
}

/**
 * @param {string} clientId
 * @param {string} callbackUri
 * @param {string=} state When client adds a state parameter to a request, GID will redirect back with the same state value.
 */
export function registerCallbackClient(clientId, callbackUri, state) {
  Cache.setItem(KEY_CALLBACK_CLIENT, clientId, {
    // expires in an hour (consistent with token expiry)
    expires: Date.now() + 60 * 60 * 1000,
  })
  Cache.setItem(KEY_CALLBACK_CLIENT_URI, callbackUri, {
    // expires in an hour (consistent with token expiry)
    expires: Date.now() + 60 * 60 * 1000,
  })
  if (state) {
    Cache.setItem(KEY_CALLBACK_CLIENT_STATE, state, {
      // expires in an hour (consistent with token expiry)
      expires: Date.now() + 60 * 60 * 1000,
    })
  } else {
    Cache.removeItem(KEY_CALLBACK_CLIENT_STATE)
  }
}

export async function unregisterCallbackClient() {
  Cache.removeItem(KEY_CALLBACK_CLIENT)
  Cache.removeItem(KEY_CALLBACK_CLIENT_URI)
  Cache.removeItem(KEY_CALLBACK_CLIENT_STATE)
}

/**
 * @returns {{id: string | null, uri: string | null, state: string | null}}
 */
export const getCallbackClient = () => ({
  id: Cache.getItem(KEY_CALLBACK_CLIENT),
  uri: Cache.getItem(KEY_CALLBACK_CLIENT_URI),
  state: Cache.getItem(KEY_CALLBACK_CLIENT_STATE),
})

export const hasCallbackClient = () =>
  !!Cache.getItem(KEY_CALLBACK_CLIENT) &&
  !!Cache.getItem(KEY_CALLBACK_CLIENT_URI)

/**
 * @param {import('services/auth/types').FederatedSignInCustomState} customState
 */
export const setFederatedSignInState = (customState) => {
  const json = JSON.stringify(customState)
  Cache.setItem(
    KEY_FEDERATED_SIGN_IN_STATE, 
    json
  )
}

/**
 * @param {{ remove?: boolean }=} options
 * @returns {import('services/auth/types').FederatedSignInCustomState | null}
 */
export const getFederatedSignInState = ({ remove = false } = { remove: false }) => {
  const json = Cache.getItem(KEY_FEDERATED_SIGN_IN_STATE) ?? '{}'
  try {
    if (remove) {
      removeFederatedSignInState()
    }
    return FederatedSignInCustomState.parse(JSON.parse(json))
  } catch (e) {
    return null
  }
}

export const removeFederatedSignInState = () => {
  Cache.removeItem(KEY_FEDERATED_SIGN_IN_STATE)
}